/** Convert VAPID public key (base64url) to Uint8Array for PushManager.subscribe. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

export type WebPushSubscriptionPayload = {
  endpoint: string;
  expirationTime?: number | null;
  keys: {
    p256dh: string;
    auth: string;
  };
};

function subscriptionToPayload(sub: PushSubscription): WebPushSubscriptionPayload | null {
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return null;
  return {
    endpoint: json.endpoint,
    expirationTime: json.expirationTime ?? null,
    keys: {
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    },
  };
}

/**
 * Register service worker, subscribe to Web Push, and POST subscription to API.
 * @param opts.interactive When true, may prompt for Notification permission (call from a user gesture).
 *   When false, only completes if permission is already granted (safe for auto-run after login).
 */
export async function subscribeToWebPush(
  registerSubscription: (payload: WebPushSubscriptionPayload) => Promise<void>,
  opts?: { interactive?: boolean },
): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  if (typeof Notification === 'undefined') return false;

  let permission = Notification.permission;
  if (permission === 'denied') return false;
  if (permission === 'default') {
    if (!opts?.interactive) return false;
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return false;

  const registration = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;

  const envKey = import.meta.env.VITE_WEB_PUSH_VAPID_PUBLIC_KEY?.trim();
  let publicKey = envKey;

  if (!publicKey) {
    const api = (await import('../services/api')).default;
    const res = await api.get('/notifications/web-push/vapid-public-key');
    publicKey = res.data?.data?.publicKey;
  }

  if (!publicKey) return false;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });
  }

  const payload = subscriptionToPayload(subscription);
  if (!payload) return false;

  await registerSubscription(payload);
  return true;
}

export async function unsubscribeFromWebPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();

  try {
    const api = (await import('../services/api')).default;
    await api.post('/notifications/web-push/unsubscribe', { endpoint });
  } catch {
    // ignore
  }
}
